import { createContext, useEffect, useState, useCallback, type ReactNode } from 'react'
import { lightTheme, darkTheme } from './themeTokens'

export type ThemeMode = 'light' | 'dark' | 'system'

interface ThemeContextValue {
  mode: ThemeMode
  resolvedTheme: 'light' | 'dark'
  setMode: (mode: ThemeMode) => void
  accentColor: string
  setAccentColor: (color: string) => void
}

export const ThemeContext = createContext<ThemeContextValue | undefined>(undefined)

const STORAGE_KEY = 'test-dashboard-theme'
const ACCENT_STORAGE_KEY = 'test-dashboard-accent'
const DEFAULT_ACCENT = '#2563eb'

function getSystemTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'light'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function getStoredMode(): ThemeMode {
  if (typeof window === 'undefined') return 'system'
  const stored = localStorage.getItem(STORAGE_KEY)
  if (stored === 'light' || stored === 'dark' || stored === 'system') {
    return stored
  }
  return 'system'
}

function getStoredAccent(): string {
  if (typeof window === 'undefined') return DEFAULT_ACCENT
  return localStorage.getItem(ACCENT_STORAGE_KEY) || DEFAULT_ACCENT
}

function generateAccentVariants(baseColor: string): { primary: string; primaryDark: string } {
  const hex = baseColor.replace('#', '')
  const r = parseInt(hex.substring(0, 2), 16)
  const g = parseInt(hex.substring(2, 4), 16)
  const b = parseInt(hex.substring(4, 6), 16)

  const darkerR = Math.max(0, Math.floor(r * 0.8))
  const darkerG = Math.max(0, Math.floor(g * 0.8))
  const darkerB = Math.max(0, Math.floor(b * 0.8))

  return {
    primary: baseColor,
    primaryDark: `#${darkerR.toString(16).padStart(2, '0')}${darkerG.toString(16).padStart(2, '0')}${darkerB.toString(16).padStart(2, '0')}`,
  }
}

function applyTheme(resolvedTheme: 'light' | 'dark', accentColor: string) {
  const root = document.documentElement
  const tokens = resolvedTheme === 'dark' ? darkTheme : lightTheme
  const accent = generateAccentVariants(accentColor)

  root.classList.remove('light', 'dark')
  root.classList.add(resolvedTheme)

  Object.entries(tokens).forEach(([property, value]) => {
    root.style.setProperty(property, value)
  })

  root.style.setProperty('--color-primary', accent.primary)
  root.style.setProperty('--color-primary-dark', accent.primaryDark)
}

interface ThemeProviderProps {
  children: ReactNode
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const [mode, setModeState] = useState<ThemeMode>(getStoredMode)
  const [accentColor, setAccentColorState] = useState<string>(getStoredAccent)
  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>(() => {
    const storedMode = getStoredMode()
    return storedMode === 'system' ? getSystemTheme() : storedMode
  })

  const setMode = useCallback((newMode: ThemeMode) => {
    setModeState(newMode)
    localStorage.setItem(STORAGE_KEY, newMode)
  }, [])

  const setAccentColor = useCallback((color: string) => {
    setAccentColorState(color)
    localStorage.setItem(ACCENT_STORAGE_KEY, color)
  }, [])

  useEffect(() => {
    const resolved = mode === 'system' ? getSystemTheme() : mode
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Intentional sync when mode changes
    setResolvedTheme(resolved)
    applyTheme(resolved, accentColor)
  }, [mode, accentColor])

  useEffect(() => {
    if (mode !== 'system') return

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleChange = () => {
      const newResolved = getSystemTheme()
      setResolvedTheme(newResolved)
      applyTheme(newResolved, accentColor)
    }

    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [mode, accentColor])

  return (
    <ThemeContext.Provider value={{ mode, resolvedTheme, setMode, accentColor, setAccentColor }}>
      {children}
    </ThemeContext.Provider>
  )
}
